import { useCallback, useMemo, useState } from 'react';
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';

import { TeamMemberRecord, useGetContactsQuery } from '@/store/contactsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { TeamMemberPreview } from '@/components/common/TeamMemberPreview';
import { paths } from '@/constants/paths';
import { minimalScrollbarStyling } from '@/constants/styles';
import { useNavigate } from 'react-router';
import { useLocalization } from '@/hooks/useLocalization';
import { FooterButton } from '@/components/common/FooterButton';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { ContentContainer, WIDGET_PADDING } from '../contentContainer';

type DashboardMyFinancialTeam = StandardWidgetProps;

const isEmbeddableBundle = import.meta.env.VITE_WIDGET === 'external';

/**
 * My Financial Team dashboard widget.
 */
const DashboardMyFinancialTeam = ({
  isHalfWidth,
  title,
  container = 'widget',
}: DashboardMyFinancialTeam) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const navigate = useNavigate();
  const [showAllAdvisors, setShowAllAdvisors] = useState(false);

  const widgetTitle = title ?? t('myFinancialTeam.title');

  const authLoaded = useAuthIsLoaded();

  const { data, isLoading, isSuccess } = useGetContactsQuery(
    authLoaded ? undefined : skipToken,
  );
  const originalLength = data?.Records.length;

  const isSmall = useMediaQuery(theme.breakpoints.down(1350));
  const isLarge = useMediaQuery(theme.breakpoints.down(1600));

  /**
   * Small & up: Keep the team members limited to a single row.
   * IsHalfWidth: Display up to 2 stacked.
   * https://wealthaccess.atlassian.net/browse/EUD-52
   */
  const numberOfContactsToDisplay = useMemo(() => {
    if (isSmall || isHalfWidth) {
      return 2;
    }

    if (isLarge) {
      return 3;
    }

    return 4;
  }, [isSmall, isHalfWidth, isLarge]);

  const displayCount =
    isEmbeddableBundle && showAllAdvisors
      ? (originalLength ?? 0)
      : numberOfContactsToDisplay;

  const useStackedList = isHalfWidth || (isEmbeddableBundle && showAllAdvisors);

  const teamMembers = useCallback(
    (members: number) => {
      // Since TeamMemberPreview has the skeletons,
      // we need to mock loading.
      if (isLoading) {
        return [
          {} as TeamMemberRecord,
          {} as TeamMemberRecord,
          {} as TeamMemberRecord,
          {} as TeamMemberRecord,
        ].slice(0, members);
      }

      if (!data) {
        return [];
      }

      return data.Records.slice(0, members);
    },
    [data, isLoading],
  );

  const members = teamMembers(displayCount);

  const showFooter =
    isSuccess &&
    (originalLength ?? 0) > numberOfContactsToDisplay &&
    !(isEmbeddableBundle && showAllAdvisors);

  const handleFooterClick = () => {
    if (isEmbeddableBundle) {
      setShowAllAdvisors(true);
      return;
    }
    void navigate(paths.investor.team);
  };

  const isExpandedEmbed = isEmbeddableBundle && showAllAdvisors;

  return (
    <ContentContainer
      sx={{
        ...(isExpandedEmbed
          ? {
              maxHeight: 600,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }
          : undefined),
      }}
      container={container}
      hasCustomPadding
    >
      <Box
        sx={{
          p: WIDGET_PADDING,
          pb: 3,
          ...(isExpandedEmbed
            ? {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }
            : {}),
        }}
      >
        <Typography
          variant='h5'
          sx={{ mb: 4 }}
        >
          {widgetTitle}
        </Typography>

        <Box
          sx={
            isExpandedEmbed
              ? {
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  ...minimalScrollbarStyling,
                }
              : undefined
          }
        >
          {useStackedList ? (
            members.map((member, index) => (
              <Box
                key={member.AdvisorId ?? index}
                sx={{
                  borderBottom:
                    isSmall || index === displayCount - 1
                      ? 'none'
                      : `1px solid ${theme.palette.divider}`,
                }}
              >
                <TeamMemberPreview
                  member={member}
                  isLoading={isLoading}
                />
              </Box>
            ))
          ) : (
            <Grid
              container
              sx={{ justifyContent: 'center' }}
            >
              {members.map((member, index) => (
                <Grid
                  size={isSmall ? 12 : 'grow'}
                  key={member.AdvisorId ?? index}
                  sx={{
                    borderRight:
                      isSmall || index === displayCount - 1
                        ? 'none'
                        : `1px solid ${theme.palette.divider}`,
                    borderBottom:
                      isSmall && index === 0
                        ? `1px solid ${theme.palette.divider}`
                        : 'none',
                  }}
                >
                  <TeamMemberPreview
                    member={member}
                    isLoading={isLoading}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>

      {showFooter && (
        <FooterButton
          buttonText={t('viewAllAdvisors', { count: originalLength ?? '' })}
          onClick={handleFooterClick}
        />
      )}
    </ContentContainer>
  );
};

export default DashboardMyFinancialTeam;
